// src/pages/system/WhatsAppSettings.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  PowerOff as DisconnectIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon
} from '@mui/icons-material';

function WhatsAppSettings() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [connecting, setConnecting] = useState(false);
  
  // Estados de conexión de WhatsApp
  const [whatsappStatus, setWhatsappStatus] = useState({ status: 'disconnected', qr: null });
  
  // Campos del formulario de prueba
  const [testForm, setTestForm] = useState({ number: '', message: '' });
  
  // Estados de notificación
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  
  // Valores de plantillas
  const [settings, setSettings] = useState({
    whatsapp_template: '',
    whatsapp_template_invoice: '',
    whatsapp_template_reminder: '',
    whatsapp_template_suspension: '',
    whatsapp_template_payment: ''
  });

  const pollIntervalRef = useRef(null);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  // Obtener estado de WhatsApp
  const fetchWhatsappStatus = useCallback(async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/whatsapp/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWhatsappStatus(data);
        
        // Si ya está conectado, detener el polling automático
        if (data.status === 'connected' && pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          showToast('¡WhatsApp vinculado y conectado con éxito!', 'success');
        }
      }
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
    }
  }, []);

  // Obtener plantillas desde finance settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings({
          whatsapp_template: data.whatsapp_template || '',
          whatsapp_template_invoice: data.whatsapp_template_invoice || '',
          whatsapp_template_reminder: data.whatsapp_template_reminder || '',
          whatsapp_template_suspension: data.whatsapp_template_suspension || '',
          whatsapp_template_payment: data.whatsapp_template_payment || ''
        });
      } else {
        showToast('Error al cargar las plantillas del servidor', 'error');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showToast('Error al conectar con la base de datos de configuraciones', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Iniciar el polling de estado si no está conectado
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    
    // Consultar inmediatamente
    fetchWhatsappStatus(true);
    
    // Polling cada 3 segundos
    pollIntervalRef.current = setInterval(() => {
      fetchWhatsappStatus(true);
    }, 3000);
  }, [fetchWhatsappStatus]);

  useEffect(() => {
    fetchSettings();
    fetchWhatsappStatus();
    
    // Iniciar polling si no está conectado en el primer render
    startPolling();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchSettings, fetchWhatsappStatus, startPolling]);

  // Manejar cambio de pestañas
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Manejar cambios en plantillas
  const handleTemplateChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  // Manejar cambios en formulario de prueba
  const handleTestFormChange = (e) => {
    const { name, value } = e.target;
    setTestForm(prev => ({ ...prev, [name]: value }));
  };

  // Iniciar conexión y solicitar código QR
  const handleConnect = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/whatsapp/connect', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showToast('Inicializando WhatsApp Web. Por favor, espera a que aparezca el código QR.', 'info');
        startPolling();
      } else {
        showToast('Error al iniciar el proceso de conexión', 'error');
      }
    } catch (error) {
      console.error('Error connecting WhatsApp:', error);
      showToast('Error de red al inicializar la conexión', 'error');
    } finally {
      setConnecting(false);
    }
  };

  // Desconectar WhatsApp
  const handleDisconnect = async () => {
    if (!window.confirm('¿Estás seguro de que deseas desconectar el WhatsApp corporativo y cerrar sesión? Tendrás que escanear el QR de nuevo.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/whatsapp/disconnect', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setWhatsappStatus({ status: 'disconnected', qr: null });
        showToast('WhatsApp desconectado y sesión cerrada exitosamente', 'success');
        startPolling();
      } else {
        showToast('Error al intentar cerrar sesión en WhatsApp', 'error');
      }
    } catch (error) {
      console.error('Error disconnecting WhatsApp:', error);
      showToast('Error de red al cerrar sesión', 'error');
    }
  };

  // Enviar mensaje de prueba
  const handleSendTestMessage = async (e) => {
    e.preventDefault();
    if (!testForm.number || !testForm.message) {
      showToast('Por favor, completa todos los campos del mensaje de prueba.', 'warning');
      return;
    }

    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/whatsapp/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testForm)
      });

      if (response.ok) {
        showToast('¡Mensaje de prueba enviado con éxito!', 'success');
        setTestForm(prev => ({ ...prev, message: '' })); // Limpiar mensaje pero mantener número
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Error al enviar mensaje de prueba', 'error');
      }
    } catch (error) {
      console.error('Error sending test message:', error);
      showToast('Error de conexión al enviar el mensaje de prueba', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  // Guardar configuraciones de plantillas
  const handleSaveTemplates = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        showToast('Plantillas guardadas correctamente en el sistema', 'success');
      } else {
        showToast('Error al guardar las plantillas', 'error');
      }
    } catch (error) {
      console.error('Error saving templates:', error);
      showToast('Error de red al guardar plantillas', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Función para insertar etiquetas dinámicas en las plantillas
  const handleInsertTag = (fieldName, tag) => {
    const textarea = document.getElementsByName(fieldName)[0];
    if (!textarea) return;

    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const text = settings[fieldName];
    const newText = text.substring(0, startPos) + tag + text.substring(endPos, text.length);

    setSettings(prev => ({ ...prev, [fieldName]: newText }));
    
    // Volver a enfocar el textarea
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(startPos + tag.length, startPos + tag.length);
    }, 50);
  };

  const getStatusColor = () => {
    switch (whatsappStatus.status) {
      case 'connected': return '#10b981'; // Verde esmeralda
      case 'connecting': return '#f59e0b'; // Amarillo ambar
      default: return '#ef4444'; // Rojo carmín
    }
  };

  const getStatusLabel = () => {
    switch (whatsappStatus.status) {
      case 'connected': return 'Conectado (Celular Activo)';
      case 'connecting': return 'Estableciendo Conexión...';
      default: return 'Desconectado';
    }
  };

  const tags = [
    { label: 'Nombre Cliente', code: '{client_name}' },
    { label: 'Nº Factura', code: '{invoice_number}' },
    { label: 'Monto Deuda', code: '{amount}' },
    { label: 'Fecha Vencimiento', code: '{due_date}' },
    { label: 'Enlace Portal', code: '{link_portal}' }
  ];

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
        <CircularProgress size={50} thickness={4} color="success" />
        <Typography variant="body1" color="textSecondary">
          Cargando configuración de WhatsApp...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1100px', mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Encabezado */}
      <Box mb={3} display="flex" alignItems="center" gap={2}>
        <Box sx={{ bgcolor: '#e8f5e9', p: 1.5, borderRadius: 3, color: '#2e7d32', display: 'flex' }}>
          <WhatsAppIcon fontSize="large" />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold">Notificaciones por WhatsApp</Typography>
          <Typography variant="body2" color="textSecondary">
            Gestiona la vinculación QR de la línea corporativa y automatiza los mensajes de cobranza y cortes.
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="success"
          textColor="inherit"
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}
        >
          <Tab icon={<WhatsAppIcon fontSize="small" />} iconPosition="start" label="Conexión QR" sx={{ fontWeight: 'bold' }} />
          <Tab icon={<SaveIcon fontSize="small" />} iconPosition="start" label="Plantillas de Mensajes" sx={{ fontWeight: 'bold' }} />
          <Tab icon={<SendIcon fontSize="small" />} iconPosition="start" label="Mensaje de Prueba" sx={{ fontWeight: 'bold' }} />
        </Tabs>

        <Box p={{ xs: 2, sm: 3, md: 4 }}>
          {/* PESTAÑA 1: CONEXIÓN QR */}
          {activeTab === 0 && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 2, height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h6" fontWeight="bold">Estado del Gateway</Typography>
                      <Chip
                        label={getStatusLabel()}
                        sx={{
                          bgcolor: `${getStatusColor()}15`,
                          color: getStatusColor(),
                          fontWeight: 'bold',
                          border: `1px solid ${getStatusColor()}30`
                        }}
                      />
                    </Box>
                    
                    <Divider />
                    
                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6 }}>
                      Para que YinoWISP envíe facturas y recordatorios automáticamente, debes vincular una cuenta de WhatsApp. 
                      Esto funciona emulando una pestaña de WhatsApp Web, por lo que **no tiene costo extra por mensaje**.
                    </Typography>

                    <Box sx={{ bgcolor: '#f1f5f9', p: 2, borderRadius: 2, display: 'flex', gap: 1.5 }}>
                      <InfoIcon color="action" fontSize="small" sx={{ mt: 0.2 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" color="textPrimary">Recomendaciones de Estabilidad:</Typography>
                        <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                          • Mantén el teléfono celular encendido y con internet Wi-Fi/Datos estable.<br />
                          • Registra tu número en YinoWISP y pide a tus clientes que lo agenden para evitar reportes por spam.<br />
                          • En caso de pérdida de conexión, el sistema reintentará reconectarse automáticamente.
                        </Typography>
                      </Box>
                    </Box>

                    <Box mt="auto" pt={2} display="flex" gap={2}>
                      {whatsappStatus.status === 'disconnected' ? (
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          size="large"
                          onClick={handleConnect}
                          disabled={connecting || whatsappStatus.status === 'connecting'}
                          startIcon={connecting ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                          sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
                        >
                          {connecting ? 'Inicializando...' : 'Iniciar Vinculación QR'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="error"
                          fullWidth
                          size="large"
                          onClick={handleDisconnect}
                          startIcon={<DisconnectIcon />}
                          sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
                        >
                          Cerrar Sesión (Desconectar)
                        </Button>
                      )}
                      
                      <IconButton 
                        onClick={() => { fetchWhatsappStatus(); showToast('Estado de WhatsApp actualizado.', 'info'); }}
                        sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '380px', height: '100%' }}>
                  {whatsappStatus.status === 'connected' ? (
                    <Box textAlign="center" py={4} display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#e8f5e9', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <WhatsAppIcon sx={{ fontSize: 45 }} />
                      </Box>
                      <Typography variant="h6" fontWeight="bold" color="textPrimary">¡Dispositivo Vinculado Exitosamente!</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ maxW: '320px', mx: 'auto' }}>
                        YinoWISP ya tiene permisos para despachar notificaciones. Los envíos automáticos de cobranza operarán de fondo.
                      </Typography>
                    </Box>
                  ) : whatsappStatus.qr ? (
                    <Box textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <Typography variant="subtitle2" color="textSecondary" fontWeight="bold" letterSpacing={1}>
                        ESCANEA ESTE CÓDIGO QR CON WHATSAPP
                      </Typography>
                      
                      <Paper elevation={1} sx={{ p: 1.5, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 3, display: 'flex' }}>
                        <img 
                          src={whatsappStatus.qr} 
                          alt="WhatsApp QR Code" 
                          style={{ width: '220px', height: '220px' }} 
                        />
                      </Paper>

                      <Typography variant="body2" color="textSecondary" sx={{ maxW: '320px', mt: 1, lineHeight: 1.5 }}>
                        1. Abre WhatsApp en tu celular.<br />
                        2. Entra a <strong>Dispositivos Vinculados</strong>.<br />
                        3. Presiona <strong>Vincular un dispositivo</strong> y escanea este código.
                      </Typography>
                    </Box>
                  ) : whatsappStatus.status === 'connecting' ? (
                    <Box textAlign="center" py={4}>
                      <CircularProgress size={50} thickness={4} color="success" />
                      <Typography variant="subtitle1" fontWeight="bold" mt={3} color="textPrimary">
                        Conectando con el Servidor de WhatsApp...
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Iniciando sesión o generando nuevo código QR...
                      </Typography>
                    </Box>
                  ) : (
                    <Box textAlign="center" py={4} sx={{ color: 'text.secondary' }}>
                      <WhatsAppIcon sx={{ fontSize: 60, opacity: 0.15, mb: 2 }} />
                      <Typography variant="body1" fontWeight="medium">Presiona "Iniciar Vinculación QR" a la izquierda</Typography>
                      <Typography variant="caption" display="block" mt={1}>
                        El sistema preparará la sesión en el servidor y te mostrará el código QR.
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Grid>
            </Grid>
          )}

          {/* PESTAÑA 2: PLANTILLAS DE MENSAJES */}
          {activeTab === 1 && (
            <form onSubmit={handleSaveTemplates}>
              <Box mb={3} display="flex" alignItems="center" gap={1.5} sx={{ bgcolor: '#eff6ff', p: 2, borderRadius: 2, border: '1px solid #bfdbfe' }}>
                <InfoIcon sx={{ color: '#2563eb' }} />
                <Typography variant="body2" color="#1e40af" sx={{ lineHeight: 1.5 }}>
                  Haz clic sobre las etiquetas grises para insertarlas de forma automática en la plantilla en la posición de tu cursor. 
                  Las etiquetas se reemplazarán dinámicamente con los datos correspondientes de cada cliente y su recibo antes del envío.
                </Typography>
              </Box>

              <Grid container spacing={4}>
                {/* Plantilla 1: Emisión de Factura */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                        Plantilla: Factura Emitida
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                        Se envía automáticamente a los clientes en cuanto se genera un nuevo recibo.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        name="whatsapp_template_invoice"
                        value={settings.whatsapp_template_invoice}
                        onChange={handleTemplateChange}
                        placeholder="Escribe el mensaje aquí..."
                      />
                      <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                        {tags.map(tag => (
                          <Chip 
                            key={tag.code}
                            label={tag.label} 
                            size="small" 
                            onClick={() => handleInsertTag('whatsapp_template_invoice', tag.code)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e2e8f0' } }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plantilla 2: Recordatorio de Pago */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                        Plantilla: Recordatorio Amistoso
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                        Se envía 3 días antes de la fecha de vencimiento del recibo de cobranza.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        name="whatsapp_template_reminder"
                        value={settings.whatsapp_template_reminder}
                        onChange={handleTemplateChange}
                        placeholder="Escribe el mensaje aquí..."
                      />
                      <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                        {tags.map(tag => (
                          <Chip 
                            key={tag.code}
                            label={tag.label} 
                            size="small" 
                            onClick={() => handleInsertTag('whatsapp_template_reminder', tag.code)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e2e8f0' } }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plantilla 3: Aviso de Corte */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                        Plantilla: Aviso de Suspensión
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                        Se envía a los clientes en el momento exacto en el que el sistema suspende su servicio.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        name="whatsapp_template_suspension"
                        value={settings.whatsapp_template_suspension}
                        onChange={handleTemplateChange}
                        placeholder="Escribe el mensaje aquí..."
                      />
                      <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                        {tags.map(tag => (
                          <Chip 
                            key={tag.code}
                            label={tag.label} 
                            size="small" 
                            onClick={() => handleInsertTag('whatsapp_template_suspension', tag.code)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e2e8f0' } }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plantilla 4: Plantilla Manual de Cobro */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                        Plantilla: Cobranza Manual
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                        Utilizada cuando envías un recordatorio individual de forma manual desde el listado de facturas.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        name="whatsapp_template"
                        value={settings.whatsapp_template}
                        onChange={handleTemplateChange}
                        placeholder="Escribe el mensaje aquí..."
                      />
                      <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                        {tags.map(tag => (
                          <Chip 
                            key={tag.code}
                            label={tag.label} 
                            size="small" 
                            onClick={() => handleInsertTag('whatsapp_template', tag.code)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e2e8f0' } }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plantilla 5: Comprobante de Pago Registrado */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                        Plantilla: Comprobante de Pago Confirmado 💳
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                        Se envía automáticamente al cliente por WhatsApp en cuanto se registra o aprueba un pago.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        name="whatsapp_template_payment"
                        value={settings.whatsapp_template_payment}
                        onChange={handleTemplateChange}
                        placeholder="Escribe el mensaje de confirmación de pago aquí..."
                      />
                      <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                        {tags.map(tag => (
                          <Chip 
                            key={tag.code}
                            label={tag.label} 
                            size="small" 
                            onClick={() => handleInsertTag('whatsapp_template_payment', tag.code)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e2e8f0' } }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  sx={{ px: 4, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                >
                  {saving ? 'Guardando...' : 'Guardar Plantillas'}
                </Button>
              </Box>
            </form>
          )}

          {/* PESTAÑA 3: MENSAJE DE PRUEBA */}
          {activeTab === 2 && (
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} md={8}>
                <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Enviar Mensaje de Prueba</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Valida la conexión y el formato de los textos enviando un WhatsApp directo a cualquier celular de prueba.
                      </Typography>
                    </Box>

                    {whatsappStatus.status !== 'connected' && (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        El servidor de WhatsApp está desconectado. Debes escanear el código QR en la primera pestaña antes de poder realizar pruebas.
                      </Alert>
                    )}

                    <form onSubmit={handleSendTestMessage} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <TextField
                        fullWidth
                        label="Número Celular del Destinatario"
                        name="number"
                        value={testForm.number}
                        onChange={handleTestFormChange}
                        required
                        placeholder="Ejemplo: 987654321"
                        helperText="Ingresa el número a 9 dígitos (para Perú se auto-antepone el código +51)."
                      />
                      
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Contenido del Mensaje"
                        name="message"
                        value={testForm.message}
                        onChange={handleTestFormChange}
                        required
                        placeholder="Hola, esto es un mensaje de prueba enviado de forma automática desde YinoWISP!"
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        color="success"
                        size="large"
                        disabled={sendingTest || whatsappStatus.status !== 'connected'}
                        startIcon={sendingTest ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                        sx={{ mt: 1, py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                      >
                        {sendingTest ? 'Enviando...' : 'Enviar WhatsApp de Prueba'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default WhatsAppSettings;
